import { SearchFieldWidget } from '../../widgets/SearchFieldWidget';
import * as React from 'react';
import styled from '@emotion/styled';
import { Link, useLocation } from 'react-router';
import { useDeepLinking } from '../../hooks/useDeepLinking';
import { PageWidget } from '../../widgets/layout/PageWidget';
import { ReadingLayoutWidget } from '../../widgets/layout/ReadingLayoutWidget';
import { DocsTopicSectionWidget, docsTopicSectionId } from '../../widgets/DocsTopicSectionWidget';
import { filterNavigationItems } from '../../widgets/navigationFilter';
import { buildDocsNavigationTree } from './shared/docs';
import { DOCS_RAIL_STICKY_TOP, IPAD_MEDIA } from '../../vars';
import { RailSurfaceWidget } from '../../widgets/RailSurfaceWidget';
import { TreeListWidget } from '../../widgets/TreeListWidget';

const topics = buildDocsNavigationTree('/docs/topics/').filter((item) => item.to !== '/docs/');
export const DocsTopicsPage: React.FC = () => {
  const topicsRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [query, setQuery] = React.useState('');
  const filtered = React.useMemo(() => filterNavigationItems(topics, query), [query]);

  useDeepLinking({ forwardRef: topicsRef, selectors: ['section[id]'], linkOnClick: false });

  return (
    <PageWidget title="Browse all topics" width={1484}>
      <S.Header>
        <h1>Browse all topics</h1>
        <p>Explore the documentation by category, or filter by a page or category name.</p>
      </S.Header>
      <ReadingLayoutWidget
        sidebarWidth={280}
        contentWidth={1180}
        mobileSidebar="stack"
        navigation={
          <S.Sidebar aria-label="Browse topics">
            <RailSurfaceWidget label="Topics">
              <S.Back to="/docs/">← Back to docs</S.Back>
              <S.Filter
                type="search"
                aria-label="Filter topics"
                placeholder="Filter topics…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setQuery('');
                }}
              />
              <nav aria-label="Topic categories">
                <S.Categories
                  items={filtered.map((item) => ({
                    key: item.key,
                    label: item.label,
                    to: `#${docsTopicSectionId(item.label)}`,
                    active: location.hash === `#${docsTopicSectionId(item.label)}`
                  }))}
                />
              </nav>
            </RailSurfaceWidget>
          </S.Sidebar>
        }
      >
        <S.Topics ref={topicsRef}>
          {!filtered.length && (
            <S.Empty role="status">No matching topics. Try a different page or category name.</S.Empty>
          )}
          {filtered.map((section) => (
            <DocsTopicSectionWidget key={section.key} section={section} />
          ))}
        </S.Topics>
      </ReadingLayoutWidget>
    </PageWidget>
  );
};

namespace S {
  export const Header = styled.header`
    margin-bottom: 24px;
    color: ${(p) => p.theme.text.paragraph};
    h1 {
      color: ${(p) => p.theme.text.heading};
      font-size: 36px;
      margin: 14px 0;
    }
  `;
  export const Back = styled(Link)`
    display: block;
    margin-bottom: 16px;
    color: ${(p) => p.theme.text.special};
    font-size: 14px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  `;
  export const Sidebar = styled.aside`
    --tree-list-font-size: 14px;
    --rail-label-font-size: 12px;
    position: sticky;
    top: ${DOCS_RAIL_STICKY_TOP}px;

    @media (max-width: ${IPAD_MEDIA}px) {
      position: static;
    }
  `;
  export const Filter = styled(SearchFieldWidget)`
    margin-bottom: 16px;
    box-shadow: none;
  `;
  export const Categories = styled(TreeListWidget)`
    @media (max-width: ${IPAD_MEDIA}px) {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
      gap: 4px 12px;
    }
  `;
  export const Topics = styled.div`
    min-width: 0;

    > section:first-of-type {
      margin-top: 0;
    }
  `;
  export const Empty = styled.p`
    color: ${(p) => p.theme.text.paragraph};
  `;
}
